import { useMutation, useQuery, UseQueryOptions } from '@tanstack/react-query';
import { ContactResponse, NoteRequest, NotesResponse } from './types';
import { createNote, deleteNote, getContact, getNotes, updateNote } from './api';
import { queryClient } from 'providers/AllProviders';
import { MetaApiResponse } from 'utils/types/CommonApiResponse';

export function useGetContact(
  interactionId?: number,
  id?: number,
  params?: Record<string, any>,
  options?: UseQueryOptions<ContactResponse, Error>
) {
  return useQuery<ContactResponse>({
    queryKey: ['agency_interaction_contact', interactionId, id, params],
    queryFn: () => getContact(interactionId!, id!, params),
    enabled: Boolean(interactionId && id),
    ...options
  });
}

export function useGetNotes(
  interactionId?: number,
  id?: number,
  options?: UseQueryOptions<{ data: NotesResponse[]; meta: MetaApiResponse }, Error>
) {
  return useQuery<{ data: NotesResponse[]; meta: MetaApiResponse }>({
    queryKey: ['agency_interaction_notes', interactionId, id],
    queryFn: () => getNotes(interactionId!, id!),
    enabled: Boolean(interactionId && id),
    ...options
  });
}

export function useCreateNote(interactionId?: number, id?: number) {
  return useMutation({
    mutationFn: (payload: NoteRequest) => createNote(interactionId!, id!, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['agency_interaction_notes', interactionId, id],
        refetchType: 'active'
      });
    }
  });
}

export function useUpdateNote(interactionId?: number, contactId?: number) {
  return useMutation({
    mutationFn: ({ id, payload }: { id: number; payload: NoteRequest }) =>
      updateNote(interactionId!, contactId!, id!, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['agency_interaction_notes', interactionId, contactId],
        refetchType: 'active'
      });
    }
  });
}

export function useDeleteNote(interactionId?: number, contactId?: number) {
  return useMutation({
    mutationFn: (noteId: number) => deleteNote(interactionId!, contactId!, noteId!),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['agency_interaction_notes', interactionId, contactId],
        refetchType: 'active'
      });
    }
  });
}
